import React from 'react';

// Halloween-only SVG artwork. Keep this file inside the removable theme folder.

// A shared stroke weight keeps the small details crisp on high-density screens.
const glyphs = {
  moon: <path d="M26 19.8A11.6 11.6 0 0 1 12.2 6 11.6 11.6 0 1 0 26 19.8Z" />,
  star: <path d="m16 3 3.4 9.6L29 16l-9.6 3.4L16 29l-3.4-9.6L3 16l9.6-3.4Z" />,
  bat: <path fill="currentColor" strokeWidth="0.6" d="m16 13-3-4-1 5C8 12 5 10 2 7c0 7 2 11 6 13 3-3 5-1 8 5 3-6 5-8 8-5 4-2 6-6 6-13-3 3-6 5-10 7l-1-5Z" />,
  skeleton: <><circle cx="16" cy="6.5" r="4.5" /><circle cx="14.3" cy="6" r="0.65" fill="currentColor" stroke="none" /><circle cx="17.7" cy="6" r="0.65" fill="currentColor" stroke="none" /><path d="M14.5 9.7v2.1M17.5 9.7v2.1M16 11v11M11.5 13.5h9M12.2 15.7h7.6M12.8 18h6.4M16 14l-5.5 6M16 14l5.5 6M16 22l-4.5 7M16 22l4.5 7M13.5 22h5" /></>,
  pumpkin: <><path d="M16 10C9 5 4 10 4 19c0 7 6 10 12 8 6 2 12-1 12-8 0-9-5-14-12-9Z" /><ellipse cx="16" cy="19" rx="5.5" ry="9" /><path d="M16 10V6c0-3 3-4 5-3M17 7c4-2 7-1 7 2-3 2-5 1-7-2Z" /></>,
  flower: <><path d="M16 6c-5-6-11 0-7 5-7 0-7 8-1 9-2 6 5 9 8 4 3 5 10 2 8-4 6-1 6-9-1-9 4-5-2-11-7-5Z" /><circle cx="16" cy="15" r="3.3" /></>,
  bow: <><path d="M14 14C9 5 3 6 4 14s7 6 10 2m4-2c5-9 11-8 10 0s-7 6-10 2M13 18 9 28l5-2 2 3 2-11m1 1 4 9 1-4 4 1-6-9" /><rect x="13" y="12" width="6" height="7" rx="2" /></>,
  truck: <><path d="M3 8h16v15H3ZM19 13h6l4 5v5H19M24 14v5h5M7 12h7M7 16h4" /><circle cx="9" cy="24" r="3" fill="var(--ivory)" /><circle cx="24" cy="24" r="3" fill="var(--ivory)" /></>,
  search: <><circle cx="14" cy="14" r="8.5" /><path d="m20 20 7 7" /></>,
  pin: <><path d="M26 13c0 8-10 16-10 16S6 21 6 13a10 10 0 1 1 20 0Z" /><circle cx="16" cy="13" r="3.5" /></>,
  clock: <><circle cx="16" cy="16" r="12" /><path d="M16 8v8l5 3" /></>,
  mail: <><rect x="3" y="6" width="26" height="20" rx="3" /><path d="m4 8 12 9L28 8M4 25l8-10m16 10-8-10" /></>,
};

export function CatalogGlyph({ name, className = '' }) {
  return (
    <svg className={`catalog-glyph ${className}`} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {glyphs[name] || glyphs.star}
    </svg>
  );
}

export function SpiderWeb({ className = '' }) {
  return (
    <svg className={`spider-web ${className}`} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="0.85" strokeLinejoin="round">
        <path d="M32 2V62M2 32H62M11 11 53 53M11 53 53 11" />
        <path d="M32 7Q38 17 49.7 14.3Q47 26 57 32Q47 38 49.7 49.7Q38 47 32 57Q26 47 14.3 49.7Q17 38 7 32Q17 26 14.3 14.3Q26 17 32 7Z" />
        <path d="M32 15Q36 22 44 20Q42 28 49 32Q42 36 44 44Q36 42 32 49Q28 42 20 44Q22 36 15 32Q22 28 20 20Q28 22 32 15ZM32 23Q34 27 38.4 25.6Q37 30 41 32Q37 34 38.4 38.4Q34 37 32 41Q30 37 25.6 38.4Q27 34 23 32Q27 30 25.6 25.6Q30 27 32 23Z" />
      </g>
    </svg>
  );
}

const cornerWebRays = [93, 104.5, 117, 130.5, 145, 159, 172.5, 185].map((degrees, i) => {
  const angle = degrees * Math.PI / 180;
  const tension = [0.99, 1.025, 0.975, 1.018, 0.988, 1.03, 0.97, 1.01][i];
  return { x: Math.cos(angle) * tension, y: Math.sin(angle) * tension, angle };
});

export function HeroCornerWeb() {
  const maskId = React.useId();
  const gradientId = `${maskId}-silk`;
  const hub = { x: 238, y: -13 };
  const rayLengths = [292, 306, 298, 314, 302, 320, 296, 310];
  const rays = cornerWebRays.map((ray, i) => {
    const bend = (i % 2 ? 1 : -1) * (2.2 + i * 0.24);
    const endX = hub.x + ray.x * rayLengths[i];
    const endY = hub.y + ray.y * rayLengths[i];
    return `M${hub.x} ${hub.y} Q${hub.x + ray.x * 122 - Math.sin(ray.angle) * bend} ${hub.y + ray.y * 122 + Math.cos(ray.angle) * bend} ${endX} ${endY}`;
  });
  const ringRadii = [24, 46, 71, 99, 131, 168, 210, 258];
  const rings = ringRadii.map((radius, ring) => {
    const points = cornerWebRays.map((ray, i) => {
      const wobble = Math.sin((ring + 1) * 1.7 + i * 2.15) * (1.15 + ring * 0.15);
      return {
        x: hub.x + ray.x * (radius + wobble),
        y: hub.y + ray.y * (radius + wobble),
      };
    });
    return `M${points[0].x} ${points[0].y}` + points.slice(0, -1).map((p, i) => {
      const next = points[i + 1];
      const sag = 0.425 + ((i * 2 + ring) % 4) * 0.007;
      const controlX = hub.x + ((p.x - hub.x) + (next.x - hub.x)) * sag;
      const controlY = hub.y + ((p.y - hub.y) + (next.y - hub.y)) * sag;
      return `Q${controlX} ${controlY} ${next.x} ${next.y}`;
    }).join('');
  });
  const brokenThreads = [
    'M109 78 Q99 91 86 102',
    'M67 123 Q52 132 39 145',
    'M151 132 Q139 147 126 159',
  ];
  return (
    <>
      <svg className="hero-corner-web" viewBox="0 0 240 210" fill="none" stroke="currentColor" strokeLinecap="round" aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id={gradientId} cx={hub.x} cy={hub.y} r="285" gradientUnits="userSpaceOnUse">
            <stop offset="0.42" stopColor="white" />
            <stop offset="0.78" stopColor="white" stopOpacity="0.86" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id={maskId}><rect width="240" height="210" fill={`url(#${gradientId})`} stroke="none" /></mask>
        </defs>
        <g mask={`url(#${maskId})`}>
          <g stroke="rgba(24, 17, 30, 0.25)" strokeWidth="1.55">
            {[...rays, ...rings, ...brokenThreads].map((d, i) => <path key={i} d={d} vectorEffect="non-scaling-stroke" />)}
          </g>
          <g strokeWidth="0.62" opacity="0.76">{rays.map((d, i) => <path key={i} d={d} vectorEffect="non-scaling-stroke" />)}</g>
          <g strokeWidth="0.78">{rings.map((d, i) => <path key={i} d={d} opacity={0.96 - i * 0.035} vectorEffect="non-scaling-stroke" />)}</g>
          <g strokeWidth="0.5" opacity="0.56">{brokenThreads.map((d, i) => <path key={i} d={d} vectorEffect="non-scaling-stroke" />)}</g>
        </g>
      </svg>
      <span className="hero-corner-spider" aria-hidden="true">
        <SpiderArtwork className="hero-corner-spider-artwork" />
      </span>
    </>
  );
}

const spiderLegPairs = [
  'M16 15Q12 12 10 10L7 4M15 22Q10 23 7 26L3 33M25 18Q30 16 33 17L38 20M24 26Q28 29 29 33L30 40',
  'M15 18Q10 16 7 17L2 20M16 26Q12 29 11 33L10 40M24 15Q28 12 30 10L33 4M25 22Q30 23 33 26L37 33',
];

export function SpiderArtwork({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 40 44" aria-hidden="true" focusable="false">
      {spiderLegPairs.map((legs, i) => (
        <g key={i} className={`spider-leg-pair spider-leg-pair--${i + 1}`} fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path className="spider-leg-outline" d={legs} />
          <path className="spider-legs" d={legs} />
        </g>
      ))}
      <g fill="currentColor" stroke="rgba(230, 205, 167, 0.5)" strokeWidth="0.6">
        <path d="M20 14c-7 0-7.5 8-5 13 1.2 2.7 3 5 5 5s3.8-2.3 5-5c2.5-5 2-13-5-13Z" />
        <ellipse cx="20" cy="11" rx="4.3" ry="5" />
      </g>
      <path d="M18 19Q16 23 18 26" fill="none" stroke="#9a806a" strokeWidth="0.8" strokeLinecap="round" />
    </svg>
  );
}

export function BouquetWebAccent({ side = 'left', depth = 'standard' }) {
  return (
    <span className={`bouquet-web-accent bouquet-web-accent--${side} bouquet-web-accent--${depth}`} aria-hidden="true">
      <svg className="bouquet-web-silk" viewBox="0 0 120 112" fill="none" stroke="currentColor" strokeLinecap="round" focusable="false">
        <path d="M1 1C30 8 54 31 71 57M1 1C12 32 35 58 68 77M1 1C4 39 7 70 17 105M1 1C40 2 77 4 116 15" />
        <path d="M22 5Q13 18 9 31M44 15Q30 31 24 50M65 34Q49 51 43 66M83 8Q72 20 66 32" />
        <path d="M9 31Q22 29 30 20M24 50Q40 48 49 36M43 66Q58 63 71 57M17 86Q39 81 57 71" />
        <path className="bouquet-web-thread" d="M71 57Q73 72 77 88" />
      </svg>
    </span>
  );
}

export function SeasonalCorners() {
  return (
    <div className="seasonal-corners" aria-hidden="true">
      <SpiderWeb className="seasonal-corner-web" />
      <SpiderWeb className="seasonal-corner-web seasonal-corner-web--right" />
    </div>
  );
}

const driftingDetails = [
  { left: '5%', delay: '-8s', duration: '21s', name: 'moon' },
  { left: '93%', delay: '-3s', duration: '25s', name: 'bat' },
  { left: '52%', delay: '-11s', duration: '27s', name: 'skeleton' },
  { left: '85%', delay: '-14s', duration: '23s', name: 'star' },
  { left: '14%', delay: '-5s', duration: '28s', name: 'bat' },
  { left: '76%', delay: '-18s', duration: '26s', name: 'moon' },
];

export function SeasonalDrift() {
  return <div className="seasonal-drift" aria-hidden="true">{driftingDetails.map((detail, i) => <span key={i} className={`seasonal-drift-item seasonal-drift-item--${detail.name}`} style={{ left: detail.left, animationDelay: detail.delay, animationDuration: detail.duration }}><CatalogGlyph name={detail.name} /></span>)}</div>;
}
